import { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Repeat, ListMusic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import useQuranStore from '../store/quranStore';

const RECITERS = [
  { id: '1', name: 'Mishary Al Afasy' },
  { id: '2', name: 'Abu Bakr Al Shatri' },
  { id: '3', name: 'Nasser Al Qatami' },
  { id: '4', name: 'Yasser Al Dosari' },
  { id: '5', name: 'Hani Ar Rifai' },
];

export default function AudioPlayer({ 
  audioData, 
  surahNo, 
  currentAyah, 
  totalAyahs,
  onAyahChange,
  onPreloadNext,
  surahName 
}) {
  const audioRef = useRef(null);
  const preloadAudioRef = useRef(null);
  const shouldAutoPlayRef = useRef(false);
  const { reciterId, setReciterId, isPlaying, setIsPlaying, autoAdvance, setAutoAdvance } = useQuranStore();
  
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [nextAudioData, setNextAudioData] = useState(null);

  // Get current audio URL based on reciter
  const getAudioUrl = useCallback((data) => {
    if (!data || !data[reciterId]) return null;
    return data[reciterId].originalUrl || data[reciterId].url;
  }, [reciterId]);

  // Track if this is the first load for auto-play
  const isFirstLoadRef = useRef(true);

  // Preload next verse audio when current verse changes
  useEffect(() => {
    const preloadNextVerse = async () => {
      if (autoAdvance && currentAyah < totalAyahs && onPreloadNext) {
        try {
          const nextData = await onPreloadNext(currentAyah + 1);
          setNextAudioData(nextData);
          
          // Preload the audio
          const preloadAudio = preloadAudioRef.current;
          const nextUrl = nextData && nextData[reciterId] 
            ? (nextData[reciterId].originalUrl || nextData[reciterId].url)
            : null;
          
          if (preloadAudio && nextUrl) {
            preloadAudio.src = nextUrl;
            preloadAudio.load();
          }
        } catch (err) {
          console.log('Preload failed:', err.message);
        }
      }
    };
    
    preloadNextVerse();
  }, [currentAyah, totalAyahs, autoAdvance, onPreloadNext, reciterId]);

  // Load and play audio when audioData or reciter changes
  useEffect(() => {
    const audio = audioRef.current;
    const url = getAudioUrl(audioData);
    
    if (!audio || !url) return;
    
    const playAudio = () => {
      audio.play().then(() => {
        setIsPlaying(true);
        shouldAutoPlayRef.current = false;
        isFirstLoadRef.current = false;
      }).catch((err) => {
        console.log('Play prevented:', err.message);
        setIsPlaying(false);
        shouldAutoPlayRef.current = false;
        isFirstLoadRef.current = false;
      });
    };

    // Load new audio
    audio.src = url;
    audio.load();
    
    // Auto-play if:
    // 1. First load (user clicked play on a verse)
    // 2. Auto-advancing from previous verse
    // 3. Reciter changed while playing
    if (shouldAutoPlayRef.current || isFirstLoadRef.current) {
      audio.addEventListener('canplaythrough', playAudio, { once: true });
    }
    
    return () => {
      audio.removeEventListener('canplaythrough', playAudio);
    };
  }, [audioData, reciterId, getAudioUrl, setIsPlaying]);

  // Handle play/pause toggle
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch((err) => {
        console.log('Play failed:', err.message);
        setIsPlaying(false);
      });
    }
  };

  // Handle audio ended event - this is the key for auto-advance
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      console.log('Audio ended. Auto-advance:', autoAdvance, 'Current:', currentAyah, 'Total:', totalAyahs);
      
      if (isLooping) {
        audio.currentTime = 0;
        audio.play();
      } else if (autoAdvance && currentAyah < totalAyahs && onAyahChange) {
        // Mark that we should auto-play the next audio
        shouldAutoPlayRef.current = true;
        // Move to next verse
        onAyahChange(currentAyah + 1);
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [isLooping, autoAdvance, currentAyah, totalAyahs, onAyahChange, setIsPlaying]);

  // Handle other audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => {
      // Only set to false if we're not auto-advancing
      if (!shouldAutoPlayRef.current) {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [setIsPlaying]);

  // Handle seek
  const handleSeek = (value) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  // Handle volume
  const handleVolumeChange = (value) => {
    const audio = audioRef.current;
    if (audio) {
      const vol = value[0];
      audio.volume = vol;
      setVolume(vol);
      setIsMuted(vol === 0);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isMuted) {
        audio.volume = volume || 0.5;
        setIsMuted(false);
      } else {
        audio.volume = 0;
        setIsMuted(true);
      }
    }
  };

  // Handle playback rate
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Navigation - these also trigger auto-play
  const handlePrev = () => {
    if (currentAyah > 1 && onAyahChange) {
      shouldAutoPlayRef.current = isPlaying;
      onAyahChange(currentAyah - 1);
    }
  };

  const handleNext = () => {
    if (currentAyah < totalAyahs && onAyahChange) {
      shouldAutoPlayRef.current = isPlaying;
      onAyahChange(currentAyah + 1);
    }
  };

  // Format time
  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audioData) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-border/40 p-4"
      data-testid="audio-player"
    >
      {/* Main audio element */}
      <audio ref={audioRef} preload="auto" />
      {/* Hidden preload audio element for next verse */}
      <audio ref={preloadAudioRef} preload="auto" style={{ display: 'none' }} />
      
      <div className="container mx-auto max-w-4xl">
        <div className="flex flex-col gap-3">
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-10">{formatTime(currentTime)}</span>
            <Slider
              value={[currentTime]}
              max={duration || 100}
              step={0.1}
              onValueChange={handleSeek}
              className="flex-1"
              data-testid="audio-progress"
            />
            <span className="text-xs text-muted-foreground w-10">{formatTime(duration)}</span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            {/* Left: Info */}
            <div className="flex-1 hidden sm:block">
              <p className="text-sm font-medium truncate">{surahName}</p>
              <p className="text-xs text-muted-foreground">
                {currentAyah === 0 ? 'Bismillah' : `Ayah ${currentAyah} of ${totalAyahs}`}
              </p>
            </div>

            {/* Center: Playback controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Loop current verse */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsLooping(!isLooping)}
                className={`h-8 w-8 ${isLooping ? 'text-primary bg-primary/10' : ''}`}
                title="Loop current verse"
                data-testid="loop-btn"
              >
                <Repeat className="w-4 h-4" />
              </Button>
              
              {/* Auto-advance toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAutoAdvance(!autoAdvance)}
                className={`h-8 w-8 ${autoAdvance ? 'text-primary bg-primary/10' : ''}`}
                title={autoAdvance ? "Auto-advance ON" : "Auto-advance OFF"}
                data-testid="auto-advance-btn"
              >
                <ListMusic className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrev}
                disabled={currentAyah <= 1}
                data-testid="prev-ayah-btn"
              >
                <SkipBack className="w-5 h-5" />
              </Button>
              
              <Button
                size="icon"
                onClick={togglePlay}
                className="w-12 h-12 rounded-full"
                data-testid="play-pause-btn"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" />
                )}
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                disabled={currentAyah >= totalAyahs}
                data-testid="next-ayah-btn"
              >
                <SkipForward className="w-5 h-5" />
              </Button>

              {/* Speed control */}
              <Select value={playbackRate.toString()} onValueChange={(v) => setPlaybackRate(parseFloat(v))}>
                <SelectTrigger className="w-16 h-8 text-xs" data-testid="speed-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.5">0.5x</SelectItem>
                  <SelectItem value="0.75">0.75x</SelectItem>
                  <SelectItem value="1">1x</SelectItem>
                  <SelectItem value="1.25">1.25x</SelectItem>
                  <SelectItem value="1.5">1.5x</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Right: Volume and reciter */}
            <div className="flex-1 flex items-center justify-end gap-2">
              {/* Reciter selector */}
              <Select value={reciterId} onValueChange={setReciterId}>
                <SelectTrigger className="w-32" data-testid="player-reciter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECITERS.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Volume */}
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={toggleMute} data-testid="mute-btn">
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={1}
                  step={0.01}
                  onValueChange={handleVolumeChange}
                  className="w-20"
                  data-testid="volume-slider"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
